import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { BrandingConfig } from './branding-config.model';

/**
 * Release flag of the branded portals, evaluated before login with the `subdomain` attribute only.
 * Off: branded subdomains are redirected to the main portal and no branding config is loaded.
 *
 * Type: release | Owner: <team> | Remove by: <YYYY-MM-DD> | Ticket: <ITEM-…>
 * FF-CLEANUP(feature-branded-portal): remove this constant and the flag check in `loadBrandingConfig()`
 */
export const BRANDED_PORTAL_FEATURE_FLAG = 'feature-branded-portal';

// Branded portals run on "<subdomain>.portal[-<env>].vaylens.com", e.g. "1komma5.portal-int.vaylens.com" -> "1komma5".
// The main portal (portal[-<env>].vaylens.com) and localhost have no subdomain.
const BRANDED_PORTAL_HOST = /^([a-z0-9-]+)\.portal(?:-[a-z]+)?\.vaylens\.com$/i;

export function extractBrandedSubdomain(hostname: string): string | null {
  return BRANDED_PORTAL_HOST.exec(hostname ?? '')?.[1]?.toLowerCase() ?? null;
}

@Injectable({
  providedIn: 'root',
})
export class BrandingConfigService {
  private readonly http = inject(HttpClient);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly brandingConfigSubject = new BehaviorSubject<BrandingConfig | null>(null);

  /** Branding config of the current subdomain; null until loaded or if there is none. */
  get brandingConfig$(): Observable<BrandingConfig | null> {
    return this.brandingConfigSubject.asObservable();
  }

  getCurrentSubdomain(): string | null {
    return extractBrandedSubdomain(this.document.location?.hostname);
  }

  /**
   * Loads the branding config of the current subdomain. A subdomain without branding config is not registered
   * as redirect URI in Keycloak, so the user is redirected to the main portal instead. The same happens while
   * the `feature-branded-portal` flag is off for the subdomain.
   *
   * @returns false if the user is redirected to the main portal, true otherwise
   */
  async loadBrandingConfig(): Promise<boolean> {
    const subdomain = this.getCurrentSubdomain();
    if (!subdomain) {
      return true;
    }
    // FF-CLEANUP(feature-branded-portal): keep the flag-ON branch, i.e. load the config right away
    const brandingEnabled = await this.featureFlagService.isOnBeforeLogin(
      BRANDED_PORTAL_FEATURE_FLAG,
      { subdomain }
    );
    if (!brandingEnabled) {
      this.redirectToMainPortal();
      return false;
    }
    try {
      const config = await firstValueFrom(
        this.http.get<BrandingConfig>(environment.brandingConfigUrl, { params: { subdomain } })
      );
      this.brandingConfigSubject.next(config);
    } catch (error) {
      if (
        error instanceof HttpErrorResponse &&
        (error.status === HttpStatusCode.NotFound || error.status === HttpStatusCode.Forbidden)
      ) {
        this.redirectToMainPortal();
        return false;
      }
    }
    return true;
  }

  private redirectToMainPortal(): void {
    const { pathname, search } = this.document.location;
    this.document.location.replace(
      `https://${environment.brandedPortalDomain}${pathname}${search}`
    );
  }
}
